import _ from "lodash";
import axios from "axios";
import { push } from "react-router-redux";
import moment from "moment";
import { getFormValues, change, initialize } from "redux-form";

import Swal from "helpers/swal";
import IMeta from "models/IMeta";
import { setLoading } from "../app/actions";
import generateQuery from "helpers/query";
import { URLS } from "constants/urlConstants";
import { generateDeliveryPdf } from "helpers/generatePdf";
import { getTranslation } from "helpers/common";
import FilterGenerator from "helpers/filter";
import { downloadDeliveryNote } from "helpers/download";
import PreferencesActions from "./preferences/actions";
import { CUSTOM_SETTINGS } from "constants/.";

export const actionTypes = {
  GET_MY_DELIVERIES_REQUEST: "GET_MY_DELIVERIES_REQUEST",
  GET_MY_DELIVERIES_SUCCESS: "GET_MY_DELIVERIES_SUCCESS",
  CREATE_DELIVERY_NOTES_REQUEST: "CREATE_DELIVERY_NOTES_REQUEST",
  CREATE_DELIVERY_NOTES_SUCCESS: "CREATE_DELIVERY_NOTES_SUCCESS",
  FORM_INIT: "FORM_INIT",
  MY_DELIVERIES_FAILURE: "MY_DELIVERIES_FAILURE",
};

export default class Actions {
  public static getMyDeliveries(meta: IMeta = {}) {
    return async (dispatch, getState) => {
      dispatch(request());

      if (!_.has(getState(), "preferences.settings.Settings")) {
        await dispatch(PreferencesActions.getPreferences());
      }

      dispatch(setLoading(true));

      const settings = _.get(getState(), "preferences.settings.Settings", {});
      const filter = JSON.parse(_.get(meta, "filter", "{}"));
      let roleId = _.get(
        filter,
        "type.value",
        settings.type ? settings.type.value || settings.type : "Seller"
      );
      const roleIds = _.get(getState(), "app.roleIds", []);
      const type = _.find(roleIds, ["value", roleId]);
      const { from, to } = _.get(settings, "harvestYear", {});

      if (_.isEmpty(filter)) {
        roleId = _.get(
          filter,
          "type.value",
          settings.type ? settings.type.value || settings.type : "Seller"
        );

        let harvestYear: any = {};
        if (from || to) {
          harvestYear = { from: from && Number(from), to: to && Number(to) };
        } else {
          harvestYear = {
            from: Number(moment().subtract(1, "years").format("YYYY")),
            to: Number(moment().format("YYYY")),
          };
        }

        const newFilter = {
          type: _.find(roleIds, ["value", roleId]),
          status: { value: false, label: getTranslation("NO") },
          harvestYear,
        };
        const Q = new FilterGenerator();
        Q.and("ShippedCompletely", false, "boolean");
        Q.and("Order/HarvestYear", newFilter.harvestYear, "numberRange");
        meta = {
          ...meta,
          filter: JSON.stringify(newFilter),
          searchString: Q.query,
        };
      }

      let query = generateQuery(meta, "(Order/Relations/any(l:l/RelationId");

      // Makes sorting by Buyer/Name always first (for grouping purpose)
      if (
        !meta.orderByKey ||
        (meta.orderByKey && meta.orderByKey.indexOf("Buyer") === -1)
      ) {
        const subQuery = "Order/Buyer/Name asc,";
        const subQueryIndex = query.indexOf("orderby") + 8;
        query = query.replace(subQuery, "");
        query =
          query.slice(0, subQueryIndex) + subQuery + query.slice(subQueryIndex);
      }

      if (!meta.pageSize) {
        const savedSettings = JSON.parse(
          localStorage.getItem("customSettings") || "{}"
        );
        _.set(
          meta,
          "pageSize",
          _.get(savedSettings, `${CUSTOM_SETTINGS.deliveryNotes}.pageSize`, 10)
        );
      }

      const url = `OrderLines?$expand=Order,Order/Relations,Order/DeliveryAddress,Order/DeliveryAddress/Country,
        Order/Buyer/PrimaryAddress/Country,Order/Seller,QuantityUnit,Item,
        ProductValue1,PriceUnit,Order/Buyer,Item/ProductType/ProductGroup
        &$select=Id,OrderId,Order/Number,Order/StateId,Order/TypeId,Order/HarvestYear,
        Order/DeliveryAddress/Name,Order/DeliveryAddress/Address1,Order/DeliveryAddress/ZipCode,
        Order/DeliveryAddress/City,Order/DeliveryAddress/Country/ISOCode,
        Order/DeliveryPeriodEnd,Order/Buyer/Name,Order/Buyer/Number,Order/Seller/Name,
        Order/Seller/Number,Order/Seller/FytosanitairNumber,Number,Quantity,QuantityUnit/Description,ShippedQuantity,
        ShippedCompletely,Item/Description,GrowerClaims,ProductValue1/Id,ProductValue1/Description,Price,
        PriceUnit/Description,OrderedAmount,Bio,Order/BuyerId,Order/SellerId,Order/SupplierId,
        Order/Buyer/PrimaryAddress/Address1, Order/Buyer/PrimaryAddress/ZipCode, Order/Buyer/PrimaryAddress/City,
        Order/Buyer/PrimaryAddress/Country/ISOCode,Order/Relations,Order/Buyer/OfficeNumber,Order/Buyer/MailAddress,
        Order/SkalNumber,PendingQuantity,QuantityUnitId,ItemId,ProductValue1Id,Order/DeliveryTypeId,
        Order/DeliveryAddressIsAlternative,Item/ProductType/ProductGroup/Code,Item/CultivarDescription
        ${query} and l/RoleId eq '${roleId}')) and Order/StateId ne 'Vervallen'`;

      try {
        const { data } = await axios.get(url);
        const myDeliveries = _.get(data, "value", []);
        let DocumentId: any = {};
        const payload = _.map(myDeliveries, (item) => {
          DocumentId = _.result(
            _.find(item.Order.Relations || [], {
              RelationId: _.get(getState(), "app.currentCustomer.Id"),
              RoleId: _.get(type, "label"),
            }),
            "DocumentId"
          );
          return {
            ...item,
            RoleId: roleId,
            DocumentId,
          };
        });
        const newMeta = {
          ...meta,
          count: +_.get(data, "odata.count", 0),
        };
        dispatch(success(payload, newMeta));
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.GET_MY_DELIVERIES_REQUEST };
    }
    function success(payload, meta) {
      return { type: actionTypes.GET_MY_DELIVERIES_SUCCESS, payload, meta };
    }
    function failure(error) {
      return { type: actionTypes.MY_DELIVERIES_FAILURE, error };
    }
  }

  public static initDeliveryNotes(objects, redirect = false) {
    return async (dispatch, getState) => {
      dispatch(request(objects));
      if (!_.has(getState(), "preferences.settings.Settings")) {
        await dispatch(PreferencesActions.getPreferences());
      }

      if (!_.get(getState(), "preferences.amStatusOptions", []).length) {
        await dispatch(PreferencesActions.getSelectOptions());
      }

      if (!_.get(getState(), "preferences.classOptions", []).length) {
        await dispatch(PreferencesActions.getClasses());
      }

      if (!_.get(getState(), "preferences.itemOptions", []).length) {
        await dispatch(PreferencesActions.getItemOptions());
      }

      const settingsClass = _.get(
        getState(),
        "preferences.settings.Settings.class"
      );
      const settingsStatus = _.get(
        getState(),
        "preferences.settings.Settings.status"
      );
      const classOptions = getState().preferences.classOptions;
      const statusOptions = getState().preferences.amStatusOptions;
      const orderClass = settingsClass
        ? _.find(classOptions, ["value", settingsClass.value || settingsClass])
        : _.find(classOptions, ["label", "I J"]);
      const orderStatus = settingsStatus
        ? _.find(statusOptions, [
            "value",
            settingsStatus.value || settingsStatus,
          ])
        : statusOptions[0];

      const orderLines = _.map(objects, (item) => ({
        id: item.Id,
        itemId: item.ItemId,
        harvestDate: _.get(item, "Order.HarvestYear"),
        code: _.get(item, "Item.ProductType.ProductGroup.Code"),
        class: orderClass,
        status: orderStatus,
        price: _.get(item, "Price"),
        maat: _.get(item, "ProductValue1.Description"),
        sizeId: _.get(item, "ProductValue1.Id"),
        cultivar:
          _.get(item, "Item.Description", "") + _.get(item, "GrowerClaims", ""),
        orderNummer: {
          label: _.get(item, "Order.Number"),
          value: item.OrderId,
        },
        bio: _.get(item, "Bio"),
        growerClaims: _.get(item, "GrowerClaims"),
        quantityUnit: _.get(item, "QuantityUnit.Description"),
        quantityUnitId: _.get(item, "QuantityUnitId"),
        quantity: _.get(item, "Quantity"),
        priceUnit: _.get(item, "PriceUnit.Description"),
        totalDelivered:
          _.get(item, "ShippedQuantity", 0) + _.get(item, "PendingQuantity", 0),
        comment: _.get(
          getState(),
          "preferences.settings.Settings.invoiceComment",
          ""
        ),
        fytosanitairNumber: _.get(item, "Order.Seller.FytosanitairNumber"),
      }));

      const deliveryDate = _.get(
        getState(),
        "preferences.settings.Settings.deliveryDate",
        {}
      );
      let date = new Date();
      const days = _.get(deliveryDate, "days.value", deliveryDate.days || 0);
      if (_.get(deliveryDate, "sign.value", deliveryDate.sign || "") === "+") {
        date = moment().add(days, "days").toDate();
      } else if (
        _.get(deliveryDate, "sign.value", deliveryDate.sign || "") === "-"
      ) {
        date = moment().subtract(days, "days").toDate();
      }
      const initialValues = {
        date,
        address: _.get(objects, "[0].Order.DeliveryAddress"),
        buyer: _.get(objects, "[0].Order.Buyer"),
        buyerId: _.get(objects, "[0].Order.BuyerId"),
        sellerId: _.get(objects, "[0].Order.SellerId"),
        supplierId: _.get(objects, "[0].Order.SupplierId"),
        skalNumber: _.get(objects, "[0].Order.SkalNumber"),
        bio: _.some(objects, "Bio"),
        roleId: _.get(objects, "[0].RoleId"),
        logo: _.get(getState(), "preferences.settings.Settings.logo", false),
        orderLines,
        chainRegistered: _.get(
          getState(),
          "preferences.settings.Settings.report",
          false
        ),
        comment: _.get(getState(), "preferences.settings.Settings.note", ""),
        deleveryTypeId: _.get(objects, "[0].Order.DeliveryTypeId"),
        fytosanitairNumber: _.get(
          objects,
          "[0].Order.Seller.FytosanitairNumber"
        ),
        kegStandardFromValue: _.get(
          getState(),
          "preferences.settings.Settings.kegStandardFromValue"
        ),
      };

      dispatch(initialize("delivery", initialValues));
      redirect && dispatch(push(URLS.DELIVERY_NOTES_URL));
    };

    function request(payload) {
      return { type: actionTypes.FORM_INIT, payload };
    }
  }

  public static initFustLines() {
    return (dispatch, getState) => {
      const { orderLines, fustLines } = getFormValues("delivery")(getState());
      const settings = _.get(getState(), "preferences.settings.Settings");
      const ownerTypes = _.get(getState(), "preferences.ownerTypes");
      const transferTypes = _.get(getState(), "preferences.transferTypes");
      const itemOptions = _.get(getState(), "preferences.itemOptions");

      for (const line of orderLines) {
        const total = _.get(line, "total", 0);
        if (total <= 0) {
          Swal.alert(getTranslation("TOTAL_GREATER_ZERO"));
          return;
        }
      }

      if (!fustLines) {
        const newFustLines = [] as any;
        const tulpTypeDeliveries = _.filter(orderLines, ["code", "TULP"]);
        const lelTypeDeliveries = _.filter(orderLines, ["code", "LEL"]);
        const otherTypeDeliveries = _.filter(
          orderLines,
          ({ code }) => !_.includes(["TULP", "LEL"], code)
        );

        const addLinePerType = (array, type) => {
          if (_.isEmpty(array)) {
            return;
          }

          const fustLine = _.get(fustLines, `${newFustLines.length}`, {});
          const settingsKind = _.get(settings, type);
          const kind = settingsKind
            ? itemOptions.find(
                (f) => f.value === (settingsKind.value || settingsKind)
              )
            : undefined;

          const line = {
            type: _.find(ownerTypes, ["value", settings?.kegStandardFromValue]),
            buyContainer: _.find(transferTypes, ["value", "NVT"]),
            fust: _.reduce(array, (sum, item) => sum + +item.fust, 0),
            ...fustLine,
            kind,
            comment: _.get(settings, "fustComment"),
            costTo: _(orderLines).map("orderNummer").uniq().value()[0],
          };
          newFustLines.push(line);

          if (type === "othersStandard" || !kind) {
            return;
          }

          let standard;

          if (kind.number === "F0005") {
            standard = "containerStandard";
          } else if (kind.number === "F0008") {
            standard = "createLilyStandard";
          }

          standard &&
            newFustLines.push({
              ...line,
              fust: 0,
              kind: _.get(settings, standard),
              comment: _.get(settings, "carrierComment"),
            });
        };

        addLinePerType(tulpTypeDeliveries, "tulipStandard");
        addLinePerType(lelTypeDeliveries, "lilyStandard");
        addLinePerType(otherTypeDeliveries, "othersStandard");
        dispatch(change("delivery", "fustLines", newFustLines));
      }
    };
  }

  public static createDeliveryNotes(deliveryNote, showCertificates = false) {
    return async (dispatch) => {
      dispatch(request());
      dispatch(setLoading(true));

      const params = {
        Date: moment.utc().format(),
        DeliveryDate: moment.utc(deliveryNote.date).format(),
        FreightCosts: _.get(deliveryNote, "transport.freightCosts.value"),
        FreeTextShipment: _.get(deliveryNote, "comment"),
        FreeTextTransporter: _.get(deliveryNote, "transport.comment"),
        Transporter: _.get(deliveryNote, "transport.transporter"),
        CityShipment: _.get(deliveryNote, "transport.cityShipment"),
        BuyerId: deliveryNote.buyerId,
        SellerId: deliveryNote.sellerId,
        SupplierId: deliveryNote.supplierId,
        SkalNumber: deliveryNote.skalNumber,
        IsFinal: false,
        Source: "MijnCNB",
        ChainRegistered: _.get(deliveryNote, "chainRegistered", false),
        Bio: deliveryNote.bio,
      };

      try {
        const { data } = await axios.post("Shipments", params);
        const { Id } = data;
        if (Id) {
          const orderLinesResult = await axios.all(
            _.get(deliveryNote, "orderLines", []).map((line, index) =>
              axios.post("ShipmentLines", {
                ShipmentId: Id,
                Number: index + 1,
                OrderLineId: line.id,
                TypeId: _.get(line, "party.value"),
                ItemId: line.itemId,
                SizeId: line.sizeId,
                ClassId: _.get(line, "class.value"),
                HarvestDate: null,
                OriginCountryId: null,
                TextInvoice: line.comment,
                CertificateNumber: line.certificate || "",
                CertificateType: "BKD",
                Status: "Open",
                ShippedQuantity: +line.total,
                ShippedQuantityUnitId: line.quantityUnitId,
                QuantityFust: +line.fust,
                QuantityPerFust: +line.perFust,
                QuantityRemainingFust: +line.restFust,
                AMStatus: _.get(line, "status.value"),
                Bio: line.bio,
                GrowerClaims: line.growerClaims,
              })
            )
          );
          const ShipmentLineId = _.get(orderLinesResult, "[0].data.Id", 0);
          const fustLines = _.get(deliveryNote, "fustLines", []).filter(
            (line) => {
              const buyContainer = _.get(line, "buyContainer.value");

              return buyContainer && buyContainer !== "NVT";
            }
          );
          await axios.all(
            fustLines.map((line, index) =>
              axios.post("ShipmentFustLines", {
                ShipmentLineId,
                Number: index + 1,
                Quantity: +line.fust,
                ItemId: _.get(line, "kind.value"),
                OwnerTypeId: _.get(line, "type.value"),
                OwnerId: null,
                Transfer: _.get(line, "buyContainer.value"),
                Price: line.price,
                FreeText: line.comment,
                OrderId: _.get(line, "costTo.value"),
              })
            )
          );
          await axios.all(
            _.get(deliveryNote, "extraCostLines", []).map((line, index) =>
              axios.post("ShipmentCostLines", {
                ShipmentLineId,
                Number: index + 1,
                Amount: +line.amount,
                ItemId: _.get(line, "article.value"),
                Specification: line.description,
                FreeText: line.comment,
                OrderId: _.get(line, "costTo.value"),
              })
            )
          );

          const customerResult = await axios.all([
            getCustomer(deliveryNote.sellerId),
            getCustomer(deliveryNote.supplierId),
          ]);

          const pdfString = await generateDeliveryPdf(
            showCertificates,
            data.DeliveryId,
            deliveryNote,
            _.get(customerResult, "[0].data.value[0]"),
            _.get(customerResult, "[1].data.value[0]")
          );

          // Upload pdf to CAPI
          await axios.put(`Shipments(${Id})/PutPDF`, pdfString.split(",")[1], {
            headers: {
              "Content-Type": "application/pdf",
            },
          });

          await axios.put(`Shipments(${Id})/Finalize`);

          Swal.confirm({
            title: getTranslation("PRINT_NOTE"),
            cancelButtonText: getTranslation("NO"),
            confirmButtonText: getTranslation("YES"),
          }).then(async (result) => {
            if (result.value) {
              const fileName = `${Id}n.pdf`;
              downloadDeliveryNote(pdfString, fileName);
            }
          });
        }

        dispatch(setLoading(false));
        dispatch(push(URLS.MY_DELIVERIES_URL));
      } catch (error) {
        const err = _.get(error, "response.data", {});
        Swal.alert(_.get(err["odata.error"], "message.value", "Error"));
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.CREATE_DELIVERY_NOTES_REQUEST };
    }
    function failure(error) {
      return { type: actionTypes.MY_DELIVERIES_FAILURE, error };
    }

    function getCustomer(id) {
      const customerUrl = `Customers?$filter=Id eq ${id}&$expand=PrimaryAddress,PrimaryAddress/Country,Certificates
        &$select=Number,Name,OfficeNumber,FaxNumber,SkalNumber,NAKTNumber,BKDNumber,MailAddress,PrimaryAddress/Address1,
        PrimaryAddress/ZipCode,PrimaryAddress/City,PrimaryAddress/Country/Description,FytosanitairNumber,
        Certificates/Participation,Certificates/RegistrationNumber,Certificates/CertificationOrganization`;
      return axios.get(customerUrl);
    }
  }
}
